import React, { useCallback, useEffect, useRef, useState } from 'react'
import { useNavigate, useParams } from 'react-router'
import { useEvent } from 'react-use-event-hook'
import { getCachedSpaceDataForAnalytics } from '@components/Analytics/useGatherSpaceDetailsAnalytics'
import { DirectMessageItemSkeleton } from '@components/DirectMessages/DirectMessageItemSkeleton'
import { useLiveSpaceState } from '@components/Live/LiveClientProvider'
import { ModalContainer } from '@components/Modals/ModalContainer'
import { ActionNavItem } from '@components/NavItem/ActionNavItem'
import { ChannelNavGroup } from '@components/NavItem/ChannelNavGroup'
import { ChannelNavItem } from '@components/NavItem/ChannelNavItem'
import { OffscreenMarker, OffscreenPill } from '@components/OffscreenPill/OffscreenPill'
import { StakingBadge } from '@components/StakingBadge/StakingBadge'
import { useReadableMembershipInfo } from '@components/TownPageLayout/useReadableMembershipInfo'
import { UpdatePill } from '@components/UpdatePill/UpdatePill'
import { useUpdatePillStateStore } from '@components/UpdatePill/useUpdatePillStateStore'
import { CreateChannelFormContainer } from '@components/Web3/CreateChannelForm'
import { useMembershipSubscriptionsStore } from '@components/Web3/Subscriptions/membershipSubscriptions.store'
import { SubscriptionMigrationToast } from '@components/Web3/Subscriptions/SubscriptionMigrationToast'
import { genId } from '@towns-protocol/sdk'
import { Badge, Box, Card, Icon, IconButton, Paragraph, Stack, Text } from '@ui'
import { LayoutGroup } from 'framer-motion'
import { useMyAbstractAccountAddress } from 'hooks/useAbstractAccountAddress'
import { Analytics } from 'hooks/useAnalytics'
import { useCreateLink } from 'hooks/useCreateLink'
import { isReduceMotion, useDevice } from 'hooks/useDevice'
import { useFeatureFlags } from 'hooks/useFeatureFlags'
import { useReviews } from 'hooks/useReviews'
import { useShortcut } from 'hooks/useShortcut'
import { useSortedChannels } from 'hooks/useSortedChannels'
import { useUnseenChannelIds } from 'hooks/useUnseenChannelIdsCount'
import { CHANNEL_INFO_PARAMS, PATHS } from 'routes'
import { useSpaceMetadataProgress } from 'routes/EditSpaceMetadata/hooks/useSpaceMetadataProgress'
import { usePanelActions } from 'routes/layouts/hooks/usePanelActions'
import { useReviewStore } from 'store/reviewStore'
import { addressFromSpaceId } from 'ui/utils/utils'
import {
    NULL_ADDRESS,
    Permission,
    SpaceData,
    useConnectivity,
    useHasPermission,
    useIsSpaceOwner,
    useSpaceDelegationOperator,
    useSpaceThreadRootsUnreadCount,
    useSpaceUnreadThreadMentions,
} from 'use-towns-client'
import { env } from 'utils'

import { useMembersNotInDMs } from './hooks/useMembersNotInDMs'
import { useOffscreenMarkers } from './hooks/useOffscreenMarkers'
import { AppVersionText } from './AppVersionText'
import { CondensedChannelNavItem } from './CondensedChannelNavItem'
import { LiveChannelSection } from './LiveChannelSection'
import { MemberNotInDMsNavItem } from './MemberNotInDMsNavItem'
import { RenewMembershipItem } from './RenewMembershipItem'
import * as styles from './SpaceSideBar.css'
import { SpaceSideBarHeader } from './SpaceSideBarHeader'
import { SpaceSideBarListItem } from './SpaceSideBarListItem'
import { SidebarLoadingAnimation } from './SpaceSideBarLoading'

type Props = {
    space: SpaceData
}

const HEADER_MARGIN = 50

export const SpaceSideBar = React.memo((props: Props) => {
    const { space } = props
    const { isTouch } = useDevice()
    const { loggedInWalletAddress } = useConnectivity()
    const { createLink } = useCreateLink()
    const { unseenChannelIds } = useUnseenChannelIds()
    const scrollRef = useRef<HTMLDivElement>(null)
    const isUpdatePillDisplaying = useUpdatePillStateStore((s) => s.isUpdatePillDisplaying)
    // call this in space sidebar to prefetch membership info
    // also used for populating analytics
    const { data: membershipInfo } = useReadableMembershipInfo(space.id)

    const spaceAddress = addressFromSpaceId(space.id)

    // Check if we should render the migration toast component:
    // 1. Space must be USDC (ETH spaces don't need migration)
    // 2. Space must not have been checked already
    const checkedMigrationSpaces = useMembershipSubscriptionsStore((s) => s.checkedMigrationSpaces)
    const shouldShowMigrationToast =
        spaceAddress &&
        membershipInfo?.currency === 'USDC' &&
        !checkedMigrationSpaces.includes(spaceAddress.toLowerCase() as `0x${string}`)

    const unreadThreadsCount = useSpaceThreadRootsUnreadCount()

    const [isCreateChannelModalVisible, setCreateChannelModalVisible] = useState(false)
    const onHideCreateChannel = useEvent(() => setCreateChannelModalVisible(false))
    const onShowCreateChannel = useEvent((e: React.MouseEvent) => {
        e.stopPropagation()
        Analytics.getInstance().track('clicked new channel', {
            spaceId: space.id,
            spaceName: space.name,
        })
        openPanel(CHANNEL_INFO_PARAMS.CREATE_CHANNEL, {
            channelFormId: `formid-${genId(5)}`,
        })
    })

    const { openPanel } = usePanelActions()

    const onTownMembersClick = useEvent(() => {
        openPanel('town-members')
    })

    const onShowBrowseChannels = useEvent(() => {
        Analytics.getInstance().track('clicked browse channels', { spaceId: space.id }, () => {
            console.log('[analytics] clicked browse channels', { spaceId: space.id })
        })
        openPanel(CHANNEL_INFO_PARAMS.BROWSE_CHANNELS)
    })

    const { hasPermission: canCreateChannel } = useHasPermission({
        spaceId: space.id,
        walletAddress: loggedInWalletAddress ?? '',
        permission: Permission.AddRemoveChannels,
    })

    const [hasScrolldedPastHeader, setHasScrolledPastHeader] = useState(false)

    const headerRef = useRef<HTMLElement>(null)

    const onScroll = useCallback(() => {
        const TOP_PADDING = 8
        const containerTop = scrollRef.current?.getBoundingClientRect().top ?? 0
        const headerTop = headerRef.current?.getBoundingClientRect()?.top ?? 0
        const headerY = headerTop - containerTop - TOP_PADDING

        setHasScrolledPastHeader(headerY < 1)
    }, [])

    const navigate = useNavigate()

    const onDisplayCreate = useCallback(
        (e?: React.MouseEvent<HTMLElement>) => {
            e?.stopPropagation()
            const link = createLink({ messageId: 'new' })
            if (link) {
                navigate(link)
            }
        },
        [createLink, navigate],
    )

    useShortcut('CreateMessage', () => onDisplayCreate())

    const params = useParams()
    const currentRouteId = params.channelSlug

    const {
        sortedUnreadChannels,
        sortedFavoriteChannels,
        sortedSpaceChannels,
        sortedDmChannels,
        spaceMemberIds,
    } = useSortedChannels({
        spaceId: space.id,
        currentRouteId,
    })

    // Get online users count from live state
    const liveSpaceState = useLiveSpaceState(space.id)
    const onlineCount = liveSpaceState?.subscribers.totalCount ?? 0

    const membersNotInDMs = useMembersNotInDMs(space.id)

    const unreadThreadMentions = useSpaceUnreadThreadMentions(space.id)

    const offscreenMarkers = useOffscreenMarkers({
        unreads: sortedUnreadChannels,
        unreadThreadsCount,
        unreadThreadMentions,
    })

    const { dismissReview, hasReviewBeenDismissed } = useReviewStore()
    const { reviews } = useReviews(space.id)
    const { data: aaAddress } = useMyAbstractAccountAddress()

    // display review prompt ?
    const hasUserReviewed = aaAddress
        ? reviews.some((review) => review.author === aaAddress)
        : false
    const shouldShowReviewPrompt =
        !hasReviewBeenDismissed(space.id) && !hasUserReviewed && env.VITE_REVIEWS_ENABLED

    const handleReviewClick = useEvent(() => {
        const { gatedSpace, pricingModule, gatedChannel } = getCachedSpaceDataForAnalytics()
        if (space?.id) {
            Analytics.getInstance().track('clicked review prompt', {
                action: 'review',
                spaceName: space.name,
                spaceId: space.id,
                gatedSpace,
                pricingModule,
                gatedChannel,
            })
        }
        openPanel(CHANNEL_INFO_PARAMS.REVIEWS)
    })

    const handleReviewDismiss = useEvent(() => {
        const { gatedSpace, pricingModule, gatedChannel } = getCachedSpaceDataForAnalytics()
        if (space?.id) {
            Analytics.getInstance().track('clicked review prompt', {
                action: 'dismiss',
                spaceName: space.name,
                spaceId: space.id,
                gatedSpace,
                pricingModule,
                gatedChannel,
            })
            dismissReview(space.id)
        }
    })

    // display finish setup prompt ?
    const { enableSpaceMetadataV2, enableUsdc } = useFeatureFlags()
    const { isOwner: isSpaceOwner } = useIsSpaceOwner(spaceAddress, aaAddress)
    const { progress: metadataProgress } = useSpaceMetadataProgress(spaceAddress)

    const shouldShowSetupPrompt =
        enableSpaceMetadataV2 && isSpaceOwner && !metadataProgress.isComplete

    const handleCompleteSetupClick = useEvent(() => {
        Analytics.getInstance().track('clicked finish setup prompt', {
            spaceId: space.id,
            spaceName: space.name,
        })

        navigate(`/${PATHS.SPACES}/${spaceAddress}/${PATHS.EDIT_SPACE_METADATA}`)
    })

    const shouldShowUpgradeButton =
        isSpaceOwner &&
        membershipInfo?.currency === 'ETH' &&
        membershipInfo?.price &&
        membershipInfo.price !== '0' &&
        enableUsdc

    const onUpgradeToUsdcClick = useEvent(() => {
        openPanel(CHANNEL_INFO_PARAMS.UPGRADE_TO_USDC)
    })

    return (
        <Card absoluteFill scroll data-testid="space-sidebar" ref={scrollRef} onScroll={onScroll}>
            <Box grow elevateReadability position="relative">
                <Stack
                    position="absolute"
                    className={styles.gradientBackground}
                    width="100%"
                    height="150"
                />

                <SpaceSideBarHeader
                    space={space}
                    opaqueHeaderBar={hasScrolldedPastHeader}
                    headerRef={headerRef as React.RefObject<HTMLDivElement>}
                />

                <Stack grow paddingY="md">
                    {space.isLoadingChannels ? (
                        <SidebarLoadingAnimation />
                    ) : (
                        <>
                            <MenuGroup>
                                {shouldShowUpgradeButton && (
                                    <ActionNavItem
                                        icon={
                                            <Icon
                                                type="arrowUp"
                                                padding="line"
                                                background="level2"
                                                color="blue"
                                                size="square_lg"
                                            />
                                        }
                                        minHeight="x5"
                                        id="upgrade"
                                        label={<Text color="accent">Upgrade To USDC</Text>}
                                        onClick={onUpgradeToUsdcClick}
                                    />
                                )}

                                {/* membership status */}
                                <RenewMembershipItem />

                                {/* delegate to node operator */}
                                {shouldShowSetupPrompt ? (
                                    <SpaceSetupPromptItem onClick={handleCompleteSetupClick} />
                                ) : shouldShowReviewPrompt ? (
                                    <SpaceSideBarListItem key="review">
                                        <ActionNavItem
                                            icon={
                                                <Icon
                                                    type="star"
                                                    padding="line"
                                                    background="level2"
                                                    color="blue"
                                                    size="square_lg"
                                                />
                                            }
                                            id="review"
                                            label={<Text color="blue">Review This Town</Text>}
                                            minHeight="x5"
                                            data-testid="review-nav-item"
                                            closeAction={handleReviewDismiss}
                                            onClick={handleReviewClick}
                                        />
                                    </SpaceSideBarListItem>
                                ) : null}

                                <DelegateToNodeOperatorItem
                                    spaceId={spaceAddress}
                                    spaceName={space.name}
                                    isSpaceOwner={!!isSpaceOwner}
                                />

                                {/* threads */}
                                <SpaceSideBarListItem key="threads">
                                    <OffscreenMarker id="threads" {...scrollMarginProps} />
                                    <ActionNavItem
                                        highlight={unreadThreadsCount > 0}
                                        icon="threads"
                                        link={`/${PATHS.SPACES}/${spaceAddress}/threads`}
                                        id="threads"
                                        label="Threads"
                                        badge={
                                            unreadThreadMentions > 0 && (
                                                <Badge value={unreadThreadMentions} />
                                            )
                                        }
                                        minHeight="x5"
                                        key="threads"
                                        data-testid="threads-nav-item"
                                    />
                                </SpaceSideBarListItem>

                                {/* mentions */}
                                <SpaceSideBarListItem key="mentions">
                                    <ActionNavItem
                                        icon="at"
                                        id="mentions"
                                        label="Mentions"
                                        link={`/${PATHS.SPACES}/${spaceAddress}/mentions`}
                                        minHeight="x5"
                                        key="mentions"
                                        data-testid="mentions-nav-item"
                                    />
                                </SpaceSideBarListItem>

                                {/* live channels */}
                                <LiveChannelSection space={space} />
                            </MenuGroup>
                            <MenuGroup>
                                <SpaceSideBarSectionHeader
                                    label="Unreads"
                                    key="unreads"
                                    hidden={sortedUnreadChannels.length === 0}
                                />
                                {sortedUnreadChannels.map((channel) => (
                                    <ItemRenderer
                                        isUnreadSection
                                        key={channel.id}
                                        itemData={channel}
                                        spaceId={space.id}
                                    />
                                ))}
                                <SpaceSideBarSectionHeader
                                    label="Favorites"
                                    key="favorites"
                                    hidden={sortedFavoriteChannels.length === 0}
                                />
                                {sortedFavoriteChannels.map((channel) => (
                                    <ItemRenderer
                                        key={channel.id}
                                        itemData={channel}
                                        spaceId={space.id}
                                    />
                                ))}

                                <SpaceSideBarSectionHeader
                                    label="Channels"
                                    badgeValue={unseenChannelIds.size}
                                    dataTestId="channels-header"
                                    headerContent={
                                        <Stack horizontal alignItems="center">
                                            <IconButton
                                                icon={
                                                    unseenChannelIds.size > 0
                                                        ? 'searchBadged'
                                                        : 'search'
                                                }
                                                tooltip="Browse channels"
                                                tooltipOptions={{ immediate: true }}
                                                onClick={onShowBrowseChannels}
                                            />

                                            {canCreateChannel && (
                                                <IconButton
                                                    icon="plus"
                                                    tooltip="New channel"
                                                    data-testid="create-channel-plus-button"
                                                    tooltipOptions={{ immediate: true }}
                                                    onClick={onShowCreateChannel}
                                                />
                                            )}
                                        </Stack>
                                    }
                                    key="channels"
                                    onClick={onShowBrowseChannels}
                                />
                            </MenuGroup>
                            <MenuGroup>
                                {sortedSpaceChannels.map((channel) => (
                                    <ItemRenderer
                                        key={channel.id}
                                        itemData={channel}
                                        spaceId={space.id}
                                    />
                                ))}

                                {canCreateChannel && (
                                    <SpaceSideBarListItem key="create-channel">
                                        <ActionNavItem
                                            icon="plus"
                                            id="newChannel"
                                            label="Create channel"
                                            data-testid="create-channel-card-button"
                                            onClick={onShowCreateChannel}
                                        />
                                    </SpaceSideBarListItem>
                                )}

                                <SpaceSideBarSectionHeader
                                    label={
                                        onlineCount > 0
                                            ? `${spaceMemberIds.length} Members · ${onlineCount} Online`
                                            : `${spaceMemberIds.length} Members`
                                    }
                                    headerContent={
                                        <IconButton
                                            size="square_sm"
                                            icon="compose"
                                            color="gray2"
                                            cursor="pointer"
                                            tooltip="New message"
                                            onClick={onDisplayCreate}
                                        />
                                    }
                                    key="direct-messages"
                                    onClick={onTownMembersClick}
                                />
                                {sortedDmChannels.map((channel) => (
                                    <ItemRenderer
                                        key={channel.id}
                                        itemData={channel}
                                        spaceId={space.id}
                                    />
                                ))}
                                {membersNotInDMs.isLoading ? (
                                    <>
                                        <DirectMessageItemSkeleton />
                                        <DirectMessageItemSkeleton />
                                        <DirectMessageItemSkeleton />
                                    </>
                                ) : (
                                    membersNotInDMs.data.map((member) => (
                                        <ItemRenderer
                                            key={member.id}
                                            itemData={member}
                                            spaceId={space.id}
                                        />
                                    ))
                                )}
                            </MenuGroup>
                        </>
                    )}
                </Stack>

                <Box
                    centerContent
                    paddingTop="md"
                    paddingX="sm"
                    paddingBottom="x4"
                    style={{ opacity: 0.5 }}
                >
                    <Paragraph size="xs" textAlign="center" color="gray2">
                        <AppVersionText />
                    </Paragraph>
                </Box>
            </Box>

            {isCreateChannelModalVisible ? (
                <ModalContainer onHide={onHideCreateChannel}>
                    <CreateChannelFormContainer spaceId={space.id} onHide={onHideCreateChannel} />
                </ModalContainer>
            ) : (
                <></>
            )}
            <OffscreenPill
                markers={offscreenMarkers.markers}
                scrollRef={scrollRef as React.RefObject<HTMLDivElement>}
                containerMarginTop={HEADER_MARGIN}
                skip={isUpdatePillDisplaying ? 'down' : undefined}
            />
            {/* the service worker won't exist in dev-mode and there's not need to check for updates */}
            {(!env.DEV || env.VITE_PUSH_NOTIFICATION_ENABLED) && !isTouch && <UpdatePill />}
            {/* Show toast if user has ETH subscription but space is now USDC */}
            {shouldShowMigrationToast && <SubscriptionMigrationToast spaceId={space.id} />}
        </Card>
    )
})

SpaceSideBar.displayName = 'SpaceSideBar'

const ItemRenderer = React.memo(function ItemRenderer(props: {
    itemData:
        | ReturnType<typeof useSortedChannels>['sortedSpaceChannels'][number]
        | ReturnType<typeof useSortedChannels>['sortedDmChannels'][number]
        | ReturnType<typeof useMembersNotInDMs>['data'][number]
    isUnreadSection?: boolean
    spaceId: string
}) {
    const { itemData, isUnreadSection, spaceId } = props
    const key = `${itemData.type}-${itemData.id}`
    // dm items can get expensive to render, use intersection observer
    const checkVisibility = itemData.type === 'dm'
    return (
        <SpaceSideBarListItem key={key} checkVisibility={checkVisibility}>
            <OffscreenMarker id={key} {...scrollMarginProps} />
            {itemData.type === 'memberNotInDMs' ? (
                <MemberNotInDMsNavItem {...itemData} key={key} />
            ) : itemData.type === 'dm' ? (
                <CondensedChannelNavItem
                    unread={itemData.unread}
                    key={key}
                    channel={itemData.channel}
                    favorite={itemData.favorite}
                    isUnreadSection={isUnreadSection}
                />
            ) : (
                <ChannelNavItem
                    key={key}
                    id={itemData.id}
                    spaceId={spaceId}
                    channelLabel={itemData.channel.label}
                    channelId={itemData.channel.id}
                    mentionCount={itemData.mentionCount}
                    favorite={itemData.favorite}
                    isUnreadSection={isUnreadSection}
                    dataTestId={`${itemData.channel.label}-channel`}
                />
            )}
        </SpaceSideBarListItem>
    )
})

ItemRenderer.displayName = 'ItemRenderer'

const scrollMarginProps = {
    scrollMarginTop: HEADER_MARGIN + 48,
    scrollMarginBottom: 48,
}

const REDUCE_MOTION = isReduceMotion()

const MenuGroup = ({ children }: { children: React.ReactNode }) =>
    REDUCE_MOTION ? children : <LayoutGroup>{children}</LayoutGroup>

const SpaceSideBarSectionHeader = (props: {
    label: string
    badgeValue?: number
    hidden?: boolean
    headerContent?: React.ReactNode
    dataTestId?: string
    onClick?: () => void
}) => {
    return props.hidden ? null : (
        <SpaceSideBarListItem key={props.label}>
            <ChannelNavGroup
                label={props.label}
                badgeValue={props.badgeValue}
                dataTestId={props.dataTestId}
                onClick={props.onClick}
            >
                {props.headerContent}
            </ChannelNavGroup>
        </SpaceSideBarListItem>
    )
}

const SpaceSetupPromptItem = ({ onClick }: { onClick: () => void }) => {
    return (
        <SpaceSideBarListItem key="finish-setup">
            <ActionNavItem
                icon={
                    <Icon
                        type="edit"
                        padding="line"
                        background="level2"
                        color="accent"
                        size="square_lg"
                    />
                }
                id="finish-town-setup"
                label={<Text color="accent">Finish setting up your Town</Text>}
                minHeight="x5"
                data-testid="finish-town-setup-nav-item"
                onClick={onClick}
            />
        </SpaceSideBarListItem>
    )
}

const DelegateToNodeOperatorItem = ({
    spaceId,
    spaceName,
    isSpaceOwner,
}: {
    spaceId: `0x${string}` | undefined
    spaceName: string
    isSpaceOwner: boolean
}) => {
    const { enableDelegation } = useFeatureFlags()

    const { isSuccess: isOperatorSuccess, data: nodeOperator } = useSpaceDelegationOperator(spaceId)

    const canBeSet =
        enableDelegation &&
        isSpaceOwner &&
        (isOperatorSuccess ? nodeOperator === NULL_ADDRESS : false)

    const [isVisible, setIsVisible] = useState(canBeSet)

    useEffect(() => {
        setIsVisible(canBeSet)
    }, [canBeSet])

    // Track when user sees the delegation prompt
    const spaceNameRef = useRef(spaceName)
    spaceNameRef.current = spaceName
    useEffect(() => {
        if (isVisible && spaceId) {
            Analytics.getInstance().track('delegation prompt', {
                spaceName: spaceNameRef.current,
                spaceId,
            })
        }
    }, [isVisible, spaceId])

    const handleDelegateToNodeDismiss = useEvent(() => {
        setIsVisible(false)
    })

    const { openPanel } = usePanelActions()

    const handleDelegateToNodeClick = useEvent(() => {
        if (spaceId) {
            Analytics.getInstance().track('clicked delegation prompt', {
                spaceName,
                spaceId,
            })
        }
        openPanel(CHANNEL_INFO_PARAMS.DELEGATE_TO_NODE_OPERATOR)
    })

    return !isVisible ? null : (
        <SpaceSideBarListItem key="delegate-to-node">
            <ActionNavItem
                icon={
                    <Box centerContent square="square_lg">
                        <StakingBadge size="x2.5" type="diamond" />
                    </Box>
                }
                id="delegate-to-node"
                label={<Text color="blue">Delegate to a Node</Text>}
                minHeight="x5"
                data-testid="delegate-to-node-nav-item"
                closeAction={handleDelegateToNodeDismiss}
                onClick={handleDelegateToNodeClick}
            />
        </SpaceSideBarListItem>
    )
}
